import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Building,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Database,
  FileText,
  BookOpen,
  Download,
  Calendar,
  FileSpreadsheet,
} from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { downloadCSV, downloadExcel } from '../../utils/reportExport';
import { downloadStyledPDF } from '../../utils/reportExport';
import { useAuth } from '../../App';
import { toast } from 'sonner';

const formatPeriodLabel = (asOfDate) => {
  if (!asOfDate) return 'As of today';
  try {
    const dt = new Date(asOfDate);
    if (Number.isNaN(dt.getTime())) return `As of ${asOfDate}`;
    return `As of ${dt.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })}`;
  } catch (_) {
    return `As of ${asOfDate}`;
  }
};

const buildExportRows = (reportData) => {
  const rows = [];
  (reportData?.vendors || []).forEach((vendor) => {
    rows.push({
      Vendor: vendor.name,
      Reference: '',
      'Bill Date': '',
      'Due Date': '',
      'Days Overdue': '',
      Current: vendor.current || 0,
      '1-30': vendor['1_30'] || 0,
      '31-60': vendor['31_60'] || 0,
      '61-90': vendor['61_90'] || 0,
      'Over 90': vendor.over_90 || 0,
      Total: vendor.total || 0,
    });
    (vendor.transactions || []).forEach((txn) => {
      rows.push({
        Vendor: `  ↳ ${vendor.name}`,
        Reference: txn.reference || '',
        'Bill Date': txn.date || '',
        'Due Date': txn.due_date || '',
        'Days Overdue': txn.days_overdue || 0,
        Current: '',
        '1-30': '',
        '31-60': '',
        '61-90': '',
        'Over 90': '',
        Total: txn.amount || 0,
      });
    });
  });
  return rows;
};

const AgedPayablesReport = ({ reportData, formatAmount, getCurrencySymbol }) => {
  const { business } = useAuth();
  const businessName = business?.name || business?.business_name || '';
  const navigate = useNavigate();
  const [expandedVendors, setExpandedVendors] = React.useState({});

  if (!reportData) return null;

  const totals = (() => {
    if (!reportData?.vendors) return {};
    const buckets = ['current', '1_30', '31_60', '61_90', 'over_90'];
    return reportData.vendors.reduce((sum, vendor) => {
      buckets.forEach((bucket) => {
        sum[bucket] = (sum[bucket] || 0) + (vendor[bucket] || 0);
      });
      sum.total = (sum.total || 0) + (vendor.total || 0);
      return sum;
    }, {});
  })();

  const toggleVendor = (vendorId) => {
    setExpandedVendors((prev) => ({ ...prev, [vendorId]: !prev[vendorId] }));
  };

  const goToBill = (txn, e) => {
    if (e) e.stopPropagation();
    if (!txn?.source_id) {
      toast.info('No linked bill found for this entry');
      return;
    }
    const type = (txn.source_type || 'bill').toLowerCase();
    // Normalize: bill_recognition, bill_payment etc → /bills/
    if (type.startsWith('invoice')) {
      navigate(`/invoices/${txn.source_id}`);
    } else {
      navigate(`/bills/${txn.source_id}`);
    }
  };

  const isGLSourced = reportData?.source === 'GL' || reportData?.source === 'journals_unified';
  const asOfDate = reportData?.as_of_date;
  const periodLabel = formatPeriodLabel(asOfDate);
  const fileSuffix = asOfDate ? `_${asOfDate}` : '';

  const handleExportCSV = () => {
    const rows = buildExportRows(reportData);
    if (rows.length === 0) {
      toast.info('Nothing to export');
      return;
    }
    downloadCSV(rows, `Aged_Payables${fileSuffix}`);
    toast.success('CSV downloaded');
  };

  const handleExportExcel = async () => {
    const rows = buildExportRows(reportData);
    if (rows.length === 0) {
      toast.info('Nothing to export');
      return;
    }
    await downloadExcel(rows, `Aged_Payables${fileSuffix}`, 'Aged Payables');
    toast.success('Excel downloaded');
  };

  const handleExportPDF = () => {
    try {
      const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); // currency stated in header
      const bucketCols = ['Vendor', 'Current', '1-30 days', '31-60 days', '61-90 days', '90+ days', 'Total'];
      const rows = (reportData.vendors || []).map((v) => [
        v.name, money(v.current), money(v['1_30']), money(v['31_60']), money(v['61_90']), money(v.over_90), money(v.total),
      ]);
      const totalRow = ['TOTAL', money(totals.current), money(totals['1_30']), money(totals['31_60']), money(totals['61_90']), money(totals.over_90), money(totals.total)]
        .map((c) => ({ content: c, styles: { fontStyle: 'bold', fillColor: [236, 253, 245] } }));
      downloadStyledPDF({
        filename: `Aged_Payables${fileSuffix}.pdf`,
        orientation: 'landscape',
        title: 'Aged Payables',
        subtitle: businessName || undefined,
        period: periodLabel,
        currency: reportData.display_currency || reportData.currency || undefined,
        note: `${(reportData.vendors || []).length} vendors with open balances - buckets by days outstanding as at the report date`,
        sections: [{
          heading: 'Ageing by vendor',
          columns: bucketCols,
          columnAlign: ['left', 'right', 'right', 'right', 'right', 'right', 'right'],
          rows: [...rows, totalRow],
        }],
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error('PDF export failed', err);
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <div className="space-y-6" data-testid="aged-payables-report">
      {/* Period header + Export */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="aged-payables-period">
          <Calendar className="w-4 h-4 text-[#108981]" />
          <span className="font-medium text-foreground">{periodLabel}</span>
          <span className="text-xs">• Aged Payables (showing outstanding balances)</span>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              data-testid="aged-payables-export-btn"
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Export
              <ChevronDown className="w-3 h-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              data-testid="aged-payables-export-pdf"
              onClick={handleExportPDF}
            >
              <FileText className="w-4 h-4 mr-2 text-red-600" />
              Export as PDF
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="aged-payables-export-excel"
              onClick={handleExportExcel}
            >
              <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
              Export as Excel
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="aged-payables-export-csv"
              onClick={handleExportCSV}
            >
              <FileText className="w-4 h-4 mr-2 text-slate-600" />
              Export as CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* GL Source Indicator */}
      {isGLSourced && (
        <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-md w-fit">
          <Database className="w-3.5 h-3.5" />
          <span>Data sourced from General Ledger</span>
        </div>
      )}

      {/* FX Breakdown Summary */}
      {reportData?.fx_breakdown && reportData.fx_breakdown.length > 0 && (
        <Card className="bg-amber-50/50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">💱</span>
              <p className="text-sm font-medium text-amber-800">Foreign Currency Breakdown</p>
            </div>
            <div className="flex flex-wrap gap-4">
              {reportData.fx_breakdown.map((fx, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-white/80 px-3 py-2 rounded-lg border border-amber-200">
                  <span className="font-mono font-bold text-amber-700">{fx.currency}</span>
                  <span className="text-amber-600">{fx.original_total?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                  <span className="text-xs text-amber-500">
                    ≈ {getCurrencySymbol()}{fx.base_total?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="bg-green-50/50 border-green-200">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Current</p>
            <p className="text-sm font-bold text-green-600 break-all">{formatAmount(totals.current)}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50/50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">1-30 Days</p>
            <p className="text-sm font-bold text-blue-600 break-all">{formatAmount(totals['1_30'])}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50/50 border-yellow-200">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">31-60 Days</p>
            <p className="text-sm font-bold text-yellow-600 break-all">{formatAmount(totals['31_60'])}</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50/50 border-orange-200">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">61-90 Days</p>
            <p className="text-sm font-bold text-orange-600 break-all">{formatAmount(totals['61_90'])}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50/50 border-red-200">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Over 90 Days
            </p>
            <p className="text-sm font-bold text-red-600 break-all">{formatAmount(totals.over_90)}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#283F8B]/5 border-[#283F8B]/20">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Payables</p>
            <p className="text-sm font-bold text-[#283F8B] break-all">{formatAmount(totals.total)}</p>
          </CardContent>
        </Card>
      </div>

      {/* GL 2000 Reconciliation */}
      {reportData?.gl_balance !== undefined && (
        <Card className="bg-slate-50/50 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-slate-600" />
              <p className="text-sm font-medium text-slate-700">GL 2000 (Trade Payables) Reconciliation</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Aged Report Total</p>
                <p className="text-sm font-semibold">{formatAmount(totals.total)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Unapplied / Adjustments</p>
                <p className={`text-sm font-semibold ${(reportData.adjustments_total || 0) !== 0 ? 'text-amber-600' : 'text-slate-500'}`} data-testid="ap-adjustments-total">
                  {formatAmount(reportData.adjustments_total || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">GL 2000 Balance</p>
                <p className="text-sm font-semibold">{formatAmount(reportData.gl_balance)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Variance</p>
                <p className={`text-sm font-semibold ${reportData.variance === 0 ? 'text-green-600' : 'text-amber-600'}`} data-testid="ap-variance-value">
                  {formatAmount(Math.abs(reportData.variance || 0))}
                  {reportData.variance !== 0 && (
                    <span className="text-xs ml-1">
                      {reportData.variance > 0 ? '(GL higher)' : '(Report higher)'}
                    </span>
                  )}
                </p>
              </div>
            </div>
            {reportData.adjustments?.length > 0 && (
              <div className="mt-3 border-t border-slate-200 pt-3" data-testid="ap-adjustments-section">
                <p className="text-xs font-medium text-slate-700 mb-2">
                  Unapplied payments &amp; adjustments (GL movements not tied to an open bill)
                </p>
                <div className="space-y-1.5">
                  {reportData.adjustments.map((adj, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 text-xs" data-testid={`ap-adjustment-row-${i}`}>
                      <span className="text-muted-foreground min-w-0">
                        <span className="font-medium text-slate-600">{adj.reference || (adj.journal_id || '').slice(0, 8)}</span>
                        {adj.date ? ` · ${adj.date}` : ''} - {adj.reason}
                      </span>
                      <span className={`font-semibold whitespace-nowrap ${adj.amount < 0 ? 'text-amber-600' : 'text-slate-700'}`}>
                        {formatAmount(adj.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {reportData.variance !== 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                Note: Variance may occur due to unallocated payments, manual journal entries, or timing differences.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vendor Details Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building className="w-5 h-5 text-[#108981]" />
            Vendor Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[250px]">Vendor</TableHead>
                <TableHead className="text-right">Current</TableHead>
                <TableHead className="text-right">1-30</TableHead>
                <TableHead className="text-right">31-60</TableHead>
                <TableHead className="text-right">61-90</TableHead>
                <TableHead className="text-right">90+</TableHead>
                <TableHead className="text-right font-bold">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reportData.vendors?.map((vendor, idx) => (
                <React.Fragment key={idx}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => toggleVendor(vendor.id || idx)}
                    data-testid={`aged-payables-vendor-row-${idx}`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {vendor.transactions?.length > 0 && (
                          expandedVendors[vendor.id || idx]
                            ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />
                        )}
                        {vendor.name}
                        {vendor.over_90 > 0 && (
                          <Badge variant="destructive" className="text-xs">Overdue</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{vendor.current ? formatAmount(vendor.current) : '-'}</TableCell>
                    <TableCell className="text-right">{vendor['1_30'] ? formatAmount(vendor['1_30']) : '-'}</TableCell>
                    <TableCell className="text-right">{vendor['31_60'] ? formatAmount(vendor['31_60']) : '-'}</TableCell>
                    <TableCell className="text-right">{vendor['61_90'] ? formatAmount(vendor['61_90']) : '-'}</TableCell>
                    <TableCell className="text-right text-red-600">{vendor.over_90 ? formatAmount(vendor.over_90) : '-'}</TableCell>
                    <TableCell className="text-right font-bold">{formatAmount(vendor.total)}</TableCell>
                  </TableRow>

                  {/* Expanded Transaction Details */}
                  <AnimatePresence>
                    {expandedVendors[vendor.id || idx] && vendor.transactions?.map((txn, txnIdx) => {
                      const clickable = !!txn.source_id;
                      return (
                        <TableRow key={`txn-${txnIdx}`} className="bg-muted/20">
                          <TableCell className="pl-10 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3" />
                              {clickable ? (
                                <button
                                  onClick={(e) => goToBill(txn, e)}
                                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm"
                                  data-testid={`aged-payables-bill-link-${idx}-${txnIdx}`}
                                  title="Open bill details"
                                >
                                  {txn.reference || `Doc #${txn.source_id?.slice(0, 8)}`}
                                </button>
                              ) : (
                                <span>{txn.reference || `Doc #${txn.document_id?.slice(0, 8)}`}</span>
                              )}
                              {txn.is_foreign_currency && (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  {txn.original_currency} {txn.original_amount?.toLocaleString()} @ {txn.fx_rate?.toLocaleString()}
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground/70 mt-1">
                              {txn.date} • Due: {txn.due_date}
                              {txn.days_overdue > 0 && (
                                <span className="text-red-500 ml-2">({txn.days_overdue} days overdue)</span>
                              )}
                            </div>
                            {/* iter872 - Reduction badges (payment vs debit-note vs reversal) */}
                            {Array.isArray(txn.reductions) && txn.reductions.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`reductions-${idx}-${txnIdx}`}>
                                {txn.reductions.map((r, i) => (
                                  <Badge key={i} variant="outline" className={`text-[10px] ${
                                    r.type === 'debit_note' ? 'bg-blue-50 text-blue-700 border-blue-200'
                                      : r.type === 'reversal' ? 'bg-slate-50 text-slate-600 border-slate-200'
                                        : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  }`}>
                                    {r.type === 'debit_note' ? 'DN' : r.type === 'reversal' ? 'Rev' : 'Pmt'} · {formatAmount(r.amount)}
                                    {r.date ? ` · ${r.date}` : ''}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell colSpan={4} className="text-sm">
                            <div className="flex items-center gap-3">
                              {txn.source_id && (
                                <button
                                  onClick={(e) => goToBill(txn, e)}
                                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                  data-testid={`view-bill-${idx}-${txnIdx}`}
                                >
                                  <FileText className="w-3 h-3" />
                                  View Bill
                                </button>
                              )}
                              {txn.journal_id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/reports/general-ledger?journal=${txn.journal_id}`);
                                  }}
                                  className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 hover:underline"
                                  data-testid={`view-journal-${idx}-${txnIdx}`}
                                >
                                  <BookOpen className="w-3 h-3" />
                                  View Journal
                                </button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            {formatAmount(txn.amount)}
                            {txn.is_foreign_currency && (
                              <div className="text-xs text-amber-600">
                                ({txn.original_currency} {txn.original_amount?.toLocaleString()})
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </AnimatePresence>
                </React.Fragment>
              ))}

              {/* Totals Row */}
              <TableRow className="bg-muted font-bold border-t-2">
                <TableCell>Total</TableCell>
                <TableCell className="text-right text-green-600">{formatAmount(totals.current)}</TableCell>
                <TableCell className="text-right text-blue-600">{formatAmount(totals['1_30'])}</TableCell>
                <TableCell className="text-right text-yellow-600">{formatAmount(totals['31_60'])}</TableCell>
                <TableCell className="text-right text-orange-600">{formatAmount(totals['61_90'])}</TableCell>
                <TableCell className="text-right text-red-600">{formatAmount(totals.over_90)}</TableCell>
                <TableCell className="text-right text-[#283F8B]">{formatAmount(totals.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Empty State */}
      {(!reportData.vendors || reportData.vendors.length === 0) && (
        <Card className="bg-muted/30">
          <CardContent className="py-12 text-center">
            <Building className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No outstanding payables found</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AgedPayablesReport;
